import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { api, unwrap, type Schemas } from '../api/client.js';
import { ScoreLine } from '../components/charts.js';
import { FullRescan } from '../components/FullRescan.js';
import { ScoreRing } from '../components/GradeRing.js';
import { HelpButton } from '../components/HelpButton.js';
import { NotFound } from '../components/Layouts.js';
import { AREA_DETAILS, TILE_TONE, useLocale, usePlan } from '../components/site.js';
import {
  LinkButton,
  LoadError,
  Loading,
  SeverityBadge,
  UrgencyBadge,
  type Severity,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDate } from '../i18n.js';

type AreaKey = keyof typeof AREA_DETAILS;
type Fix = Schemas['AreaFix'];

/**
 * Share of each area in the Health Score in per cent (monitoring-rules §6.1, HEALTH.weights in
 * packages/rules/src/thresholds.ts): a constant, as the audit decided for site-area.html.
 */
export const AREA_WEIGHT: Record<AreaKey, number> = {
  availability: 30,
  security: 25,
  seo: 15,
  links: 10,
  performance: 10,
  email: 5,
  compliance: 5,
};

/** When to act on a problem that has no explanation (it carries its own urgency otherwise). */
const URGENCY_OF: Record<Severity, 'now' | 'soon' | 'when_possible' | 'tip'> = {
  critical: 'now',
  high: 'soon',
  medium: 'soon',
  low: 'when_possible',
  info: 'tip',
};

const isArea = (value: string): value is AreaKey => Object.hasOwn(AREA_DETAILS, value);

/**
 * One area of a site (T-35, site-area.html): what we found, what it means for the business, what to
 * do and what it brings – in words and as "now → after" numbers per problem (audit).
 */
export function Area() {
  const { siteId = '', area = '' } = useParams();
  if (!isArea(area)) return <NotFound />;
  return <AreaPage siteId={siteId} area={area} />;
}

function AreaPage({ siteId, area }: { siteId: string; area: AreaKey }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const plan = usePlan();
  const query = useQuery({
    queryKey: ['site', siteId, 'area', area, locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/areas/{area}', {
          params: { path: { siteId, area }, query: { locale } },
        }),
      ),
    refetchInterval: 60_000,
  });

  if (query.isPending) return <Loading />;
  if (query.isError)
    return (
      <LoadError message={errorMessage(query.error, t)} onRetry={() => void query.refetch()} />
    );
  const a = query.data;
  const score = a.score;

  return (
    <>
      <div className="area-hd">
        <Link className="crumb" to={`/sites/${siteId}`}>
          <span aria-hidden="true">← </span>
          {t('area.back')}
        </Link>
        <h1>
          {t(`site.tiles.${area}`)}{' '}
          <span className={`badge ${TILE_TONE[a.tile.status]}`}>
            {t(`tileStatus.${a.tile.status}`)}
          </span>
        </h1>
        <p className="sub">{t('area.purpose', { why: a.why })}</p>
      </div>

      <AreaScore area={area} score={score?.now ?? null} trend={a.trend} />

      <div className="four">
        <div>
          <b>{t('area.now')}</b>
          <p>
            {a.tile.headline}
            {a.tile.sub ? ` · ${a.tile.sub}` : null}
          </p>
          {a.fixes.length > 0 ? (
            <ul>
              {a.fixes.map((f) => (
                <li key={fixKey(f)}>{f.explanation?.title ?? f.finding?.title}</li>
              ))}
            </ul>
          ) : null}
        </div>
        <div>
          <b>{t('area.means')}</b>
          <p>{a.means}</p>
        </div>
        <div>
          <b>{t('area.todo')}</b>
          {a.fixes.length === 0 ? (
            <p>{t('area.nothingToDo')}</p>
          ) : (
            <ol>
              {a.fixes.map((f) => (
                <li key={fixKey(f)}>{f.explanation?.action ?? f.finding?.title}</li>
              ))}
            </ol>
          )}
        </div>
        <div>
          <b>{t('area.gain')}</b>
          <p>
            {a.gain}
            {score && score.after > score.now
              ? ` ${t('area.gainNumbers', {
                  now: score.now,
                  after: score.after,
                  siteNow: score.siteNow,
                  siteAfter: score.siteAfter,
                  gradeNow: score.gradeNow,
                  gradeAfter: score.gradeAfter,
                })}`
              : null}
          </p>
        </div>
      </div>

      <section className="card" aria-labelledby="area-problems">
        <h2 id="area-problems">{t('area.problems')}</h2>
        {a.fixes.length === 0 ? (
          <p className="bodyM">{t('area.noProblems')}</p>
        ) : (
          <div className="afixes">
            {a.fixes.map((f) => (
              <FixCard key={fixKey(f)} siteId={siteId} fix={f} now={score?.now ?? null} />
            ))}
          </div>
        )}
      </section>

      <FullRescan
        siteId={siteId}
        card={{
          perDay: plan?.limits.manualScansPerDay ?? null,
          children: (
            <Link className="more" to={`/sites/${siteId}${AREA_DETAILS[area]}`}>
              {t('area.details')}
            </Link>
          ),
        }}
      />
    </>
  );
}

/** The area's score in its ring, how it moved in 30 days, its weight and the line of the days. */
function AreaScore({
  area,
  score,
  trend,
}: {
  area: AreaKey;
  score: number | null;
  trend: readonly { day: string; score: number }[];
}) {
  const { t, i18n } = useTranslation();
  const first = trend[0];
  const last = trend[trend.length - 1];
  const delta = first && last && trend.length >= 2 ? last.score - first.score : null;
  const day = (d: string) => formatDate(`${d}T12:00:00Z`, i18n.language, 'UTC');
  return (
    <section className="card">
      {score === null ? (
        <p className="bodyM">{t('area.notMeasured')}</p>
      ) : (
        <div className="ring">
          <ScoreRing score={score} label={t('area.scoreSr', { score })} />
          <div>
            <div className="h3s">{t('area.scoreOf', { score })}</div>
            {delta !== null && delta !== 0 ? (
              <div className={`cap ${delta > 0 ? 'up' : 'down'}`}>
                <span aria-hidden="true">{delta > 0 ? '▲ ' : '▼ '}</span>
                {delta > 0 ? `+${delta}` : `−${Math.abs(delta)}`} <span>{t('area.in30')}</span>
              </div>
            ) : null}
            <div className="cap">{t('area.weight', { share: AREA_WEIGHT[area] })}</div>
          </div>
        </div>
      )}
      {first && last && trend.length >= 2 ? (
        <ScoreLine
          width={600}
          points={trend.map((p) => p.score)}
          label={t('area.trendDescription', {
            from: day(first.day),
            to: day(last.day),
            start: first.score,
            end: last.score,
          })}
        />
      ) : score === null ? null : (
        <p className="hint">{t('area.trendEmpty')}</p>
      )}
    </section>
  );
}

const fixKey = (f: Fix) => f.finding?.id ?? f.incident?.id ?? '';

/** One problem of the area: what, its effect on the area, who can fix it and the way on. */
function FixCard({ siteId, fix, now }: { siteId: string; fix: Fix; now: number | null }) {
  const { t } = useTranslation();
  const e = fix.explanation;
  const severity = fix.finding?.severity ?? fix.incident?.severity;
  const urgency = e?.urgency ?? (severity ? URGENCY_OF[severity] : null);
  // A guide to do it yourself, else a developer; we, when there is help to order.
  const who = [e && e.diySteps.length > 0 ? 'you' : 'developer', ...(fix.service ? ['us'] : [])];
  const tone: Record<string, string> = { you: 'ok', developer: 'neutral', us: 'acc' };
  return (
    <article className="card afix">
      <div className="acts">
        {severity ? <SeverityBadge severity={severity} /> : null}
        {urgency ? <UrgencyBadge urgency={urgency} /> : null}
      </div>
      <h3 className="h4s">{e?.title ?? fix.finding?.title}</h3>
      {e ? (
        <>
          <p className="bodyM">{e.what}</p>
          <p className="bodyM">{e.impact}</p>
        </>
      ) : null}
      {now !== null && fix.areaAfter !== null && fix.areaAfter > now ? (
        <div
          className="before-after"
          role="img"
          aria-label={t('area.barsSr', { now, after: fix.areaAfter })}
        >
          <div aria-hidden="true">
            <span>{t('area.before')}</span>
            <b>{now}</b>
          </div>
          <span className="arr" aria-hidden="true">
            →
          </span>
          <div aria-hidden="true">
            <span>{t('area.after')}</span>
            <b className="after">{fix.areaAfter}</b>
          </div>
        </div>
      ) : null}
      <div>
        <span className="lab">{t('area.whoCan')}</span>
        <div className="who">
          {who.map((w) => (
            <span key={w} className={`badge ${tone[w]} nodot`}>
              {t(`area.who.${w}`)}
            </span>
          ))}
        </div>
      </div>
      <div className="acts">
        {fix.finding ? (
          <LinkButton
            to={`/sites/${siteId}/findings?open=${encodeURIComponent(fix.finding.ruleCode)}`}
            variant="secondary"
            size="sm"
          >
            {t('area.showPages')}
          </LinkButton>
        ) : null}
        {fix.service ? (
          <HelpButton
            siteId={siteId}
            service={fix.service}
            size="sm"
            {...(fix.finding ? { findingId: fix.finding.id, ruleCode: fix.finding.ruleCode } : {})}
          />
        ) : null}
      </div>
    </article>
  );
}
